import { MoonfallCloud } from '../convex-client.js';
import { SoundEffects } from './sound-effects.js';
let StellarAssets = {};


StellarAssets = (function () {
  let assetsByCardId = new Map();
  let myPendingClaimCardId = null;
  const lastStatusByCardId = new Map();
  const GENERATING_STATUSES = new Set(['queued', 'pending', 'generating', 'running']);

  function normalizeAsset(asset) {
    if (!asset?.cardId) return null;
    const status = String(asset.status || asset.queueStatus || '').trim().toLowerCase();
    return {
      ...asset,
      status: status || asset.status || null,
    };
  }

  function reset() {
    assetsByCardId = new Map();
    myPendingClaimCardId = null;
    lastStatusByCardId.clear();
  }

  function get(cardId) {
    if (!cardId) return null;
    return assetsByCardId.get(cardId) || null;
  }

  function hasAsset(cardId) {
    return assetsByCardId.has(cardId);
  }

  function isGenerating(cardId) {
    const asset = get(cardId);
    if (!asset) return false;
    return GENERATING_STATUSES.has(String(asset.status || '').toLowerCase());
  }

  function isFailed(cardId) {
    const asset = get(cardId);
    return String(asset?.status || '').toLowerCase() === 'failed';
  }

  function isReady(cardId) {
    const asset = get(cardId);
    return String(asset?.status || '').toLowerCase() === 'complete' && !!asset.videoUrl && !!asset.audioUrl;
  }

  function applyMediaToCard(card, asset) {
    if (!card || !asset || String(asset.status || '').toLowerCase() !== 'complete') return card;

    setMediaUrl(card, 'stellarVideoUrl', asset.videoUrl);
    setMediaUrl(card, 'stellarVideoWebmUrl', asset.videoWebmUrl);
    if (String(card.type || '').toLowerCase() === 'spell') {
      delete card.stellarSummonVideoUrl;
      delete card.stellarSummonVideoWebmUrl;
      setMediaUrl(card, 'stellarCastVideoUrl', asset.audioUrl);
      setMediaUrl(card, 'stellarCastVideoWebmUrl', asset.audioWebmUrl);
    } else {
      delete card.stellarCastVideoUrl;
      delete card.stellarCastVideoWebmUrl;
      setMediaUrl(card, 'stellarSummonVideoUrl', asset.audioUrl);
      setMediaUrl(card, 'stellarSummonVideoWebmUrl', asset.audioWebmUrl);
    }
    return card;
  }

  function setMediaUrl(card, field, value) {
    if (value) card[field] = value;
    else delete card[field];
  }

  function listPendingCardIds() {
    return Array.from(assetsByCardId.values())
      .filter((asset) => GENERATING_STATUSES.has(String(asset?.status || '').toLowerCase()))
      .map((asset) => asset.cardId)
      .filter(Boolean);
  }

  function getMyPendingClaimCardId() {
    return myPendingClaimCardId;
  }

  function setMyPendingClaimCardId(cardId) {
    myPendingClaimCardId = cardId ? String(cardId) : null;
  }

  async function loadMyPendingClaim() {
    if (!MoonfallCloud?.convex) return myPendingClaimCardId;
    const claim = await MoonfallCloud.convex.query('stellar:getMyPendingClaim', {});
    setMyPendingClaimCardId(claim?.cardId || null);
    return myPendingClaimCardId;
  }

  function dispatchStatusEvent(eventName, asset) {
    if (!eventName || !asset?.cardId || typeof window.CustomEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent(eventName, { detail: { cardId: asset.cardId, asset } }));
  }

  /*
   * WHAT A CARD SHOWS OF ITS PRINT CHANGED: SAID ONCE, WHOEVER READ IT. The
   * Collection's poll, its open, a card detail, the Decks tab, the packs and
   * the Exchange all read this store, and a print finishes on the server
   * whichever of them reads it next. Only the Collection's poll used to turn
   * its own read into a rebuilt tile, so a print another read saw finish kept
   * its "Attuning" chip (the owner, 21 October 2026, on production). The
   * estimate is not part of it: it drifts with the queue, and nothing is
   * rebuilt for that (the Collection's detail patches its own).
   */
  function shownState(asset) {
    if (!asset) return null;
    return JSON.stringify([
      asset.status || null,
      asset.queueStatus || null,
      asset.videoUrl || null,
      asset.videoWebmUrl || null,
      asset.audioUrl || null,
      asset.audioWebmUrl || null,
      asset.lastError || asset.error || null,
    ]);
  }

  function dispatchChanged(cardIds) {
    if (cardIds.length === 0 || typeof window.CustomEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent('moonfall:stellar-assets-changed', { detail: { cardIds } }));
  }

  /** Into the store; true when what the card shows of its print changed. */
  function store(rawAsset) {
    const asset = normalizeAsset(rawAsset);
    if (!asset?.cardId) return false;
    const previous = assetsByCardId.get(asset.cardId) || null;
    handleStatusTransition(previous, asset);
    assetsByCardId.set(asset.cardId, asset);
    return shownState(previous) !== shownState(asset);
  }

  function handleStatusTransition(previous, next) {
    if (!next?.cardId) return;
    const prevStatus = previous?.status || lastStatusByCardId.get(next.cardId);
    const nextStatus = next.status;
    lastStatusByCardId.set(next.cardId, nextStatus);

    if (
      next.cardId === myPendingClaimCardId
      && (nextStatus === 'complete' || nextStatus === 'failed')
    ) {
      myPendingClaimCardId = null;
    }

    const wasGenerating = GENERATING_STATUSES.has(prevStatus);
    if (!wasGenerating || prevStatus === nextStatus) return;

    if (nextStatus === 'complete') {
      if (SoundEffects?.play && SoundEffects.isMuted?.() !== true) {
        try { SoundEffects.play('magic'); } catch (err) { console.warn('[StellarAssets] Failed to play magic sound:', err); }
      }
      dispatchStatusEvent('moonfall:stellar-asset-ready', next);
      return;
    }

    if (nextStatus === 'failed') {
      dispatchStatusEvent('moonfall:stellar-asset-failed', next);
    }
  }

  function canRetry(cardId) {
    // Retry disabled temporarily due to rate limiting.
    return false;
    /*
    const asset = get(cardId);
    if (!asset || asset.status !== 'complete') return false;
    if (asset.retryUsed) return false;
    if (!asset.retryAvailableUntil) return false;
    return asset.retryAvailableUntil > Date.now();
    */
  }

  async function load(cardIds) {
    const ids = Array.from(new Set((cardIds || []).filter(Boolean)));
    if (ids.length === 0) return [];

    if (!MoonfallCloud?.convex) return [];

    const assets = await MoonfallCloud.convex.query('stellar:listAssets', { cardIds: ids }) || [];
    const changed = [];
    assets.forEach((rawAsset) => {
      if (store(rawAsset)) changed.push(rawAsset.cardId);
    });
    // After the whole answer is in the store: one read, one event.
    dispatchChanged(changed);
    return assets;
  }

  function update(rawAsset) {
    if (store(rawAsset)) dispatchChanged([rawAsset.cardId]);
  }

  return {
    reset,
    get,
    hasAsset,
    isGenerating,
    isFailed,
    isReady,
    applyMediaToCard,
    listPendingCardIds,
    getMyPendingClaimCardId,
    setMyPendingClaimCardId,
    loadMyPendingClaim,
    canRetry,
    load,
    update,
  };
})();

export { StellarAssets };
